<script lang="ts">
  import type {
    TableRowAction,
    TableRowActionsResolver,
  } from "@kontextwerk/web-sdk/core/layout/table"

  import { onDestroy, onMount } from "svelte"
  import { mdiRestore } from "@mdi/js"
  import {
    OrganizationsPage,
    type AdminViewFrameContext,
    type OrganizationRow,
    type OrganizationTableRowStateResolver,
    type OrganizationTableSelectionContext,
    type OrganizationsPageExtension,
    type OrganizationTableColumnExtension,
  } from "@kontextwerk/control-web-frame"
  import { adminSession } from "@kontextwerk/control-web-frame/authentication/session"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { runActionWithReauthRetry } from "@kontextwerk/web-sdk/core/security/importantActionReauth"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { requestImportantActionReauth } from "@kontextwerk/web-sdk/ui/security/importantActionReauth"
  import {
    enqueueAdminDemoResetBatch,
    getAdminDemoResetCapabilities,
    type AdminDemoResetBatchResponse,
    type AdminDemoResetCapability,
  } from "../../../api/kehrblatt/admin-demo-resets"
  import { adminDemoResetCapabilityReason, summarizeAdminDemoResetBatch } from "./_logic/quickReset"
  import { createOrganizationPerformanceController } from "./_logic/organizationPerformance.svelte"
  import OrganizationPerformanceCell from "./_modules/organization-performance-cell"

  const {
    viewFrameContext,
    activeTab,
  }: {
    viewFrameContext: AdminViewFrameContext
    activeTab: string
  } = $props()

  let capabilitiesByOrganization = $state<Record<string, AdminDemoResetCapability>>({})
  let capabilityLoading = $state(true)
  let capabilityFailed = $state(false)
  let submitting = $state(false)
  let canonicalReload = $state<(() => Promise<void>) | null>(null)
  let capabilityController: AbortController | null = null
  const performance = createOrganizationPerformanceController()
  const isPlatformAdmin = $derived($adminSession?.user?.role === "admin")
  const profitExplanation =
    "Netto-Schätzung: gespeicherte Credits plus aktuelle Monatsgrundgebühr / 30 × Tage, begrenzt auf Organisationsalter und bekanntes Vertragsende. Frühere Tarifwechsel, Trial-Zeiten, Rabatte, Gutschriften und Zahlungsausfälle werden nicht rekonstruiert. Kosten: Brief 0,75 €, WhatsApp 0,07 €, SMS 0,05 €, KI-Telefonie 0,14 €/Minute; Chatbot und Aufträge ohne Kostenabzug. Betriebs-, Zahlungsabwicklungs- und Personalkosten sind nicht enthalten."

  const loadCapabilities = async () => {
    const controller = new AbortController()
    capabilityController?.abort()
    capabilityController = controller
    capabilityLoading = true
    capabilityFailed = false
    try {
      const response = await getAdminDemoResetCapabilities({ signal: controller.signal })

      if (controller.signal.aborted) return

      capabilitiesByOrganization = Object.fromEntries(
        response.items.map((item) => [item.organization_id, item]),
      )
    } catch (err) {
      if (controller.signal.aborted) return

      console.error("admin_demo_reset_capabilities_failed", err)
      capabilitiesByOrganization = {}
      capabilityFailed = true
    } finally {
      if (capabilityController === controller) capabilityController = null

      if (!controller.signal.aborted) capabilityLoading = false
    }
  }

  const getRowState: OrganizationTableRowStateResolver = (row) => {
    const capability = capabilitiesByOrganization[row.id]

    return {
      selectable:
        !submitting &&
        !capabilityLoading &&
        !capabilityFailed &&
        capability?.eligible === true &&
        capability.state === "ready",
    }
  }

  const refreshAfterBatch = async () => {
    await Promise.all([loadCapabilities(), canonicalReload?.() ?? Promise.resolve()])
  }

  const resultNotification = (response: AdminDemoResetBatchResponse) => {
    const rejected = response.results.some((item) => item.disposition === "rejected")
    const alreadyActive = response.results.some((item) => item.disposition === "already_active")
    newNotification({
      class: rejected || alreadyActive ? "warning" : "success",
      html: `Demo-Reset: ${summarizeAdminDemoResetBatch(response)}.`,
    })
  }

  const executeBatch = async (
    organizationIds: string[],
    clearSelection: (() => void) | null,
  ): Promise<boolean> => {
    if (submitting || !organizationIds.length) return false

    submitting = true
    const idempotencyKey = crypto.randomUUID()
    try {
      const reauthConfig = {
        title: "Erneut authentifizieren",
        description:
          "Für den Demo-Reset ist aus Sicherheitsgründen eine aktuelle Anmeldung mit Ihrem Passwort erforderlich.",
        actionLabel: "Authentifizieren",
        preserveParent: true,
      }
      const response = await runActionWithReauthRetry(
        () =>
          enqueueAdminDemoResetBatch(organizationIds, {
            idempotencyKey,
          }),
        () => requestImportantActionReauth(reauthConfig),
      )

      if (!response) return false

      clearSelection?.()
      resultNotification(response)
      await refreshAfterBatch()

      return true
    } catch (err) {
      console.error("admin_demo_reset_batch_failed", err)
      newNotification({
        class: "error",
        html: "Demo-Reset konnte nicht gestartet werden.",
      })

      return false
    } finally {
      submitting = false
    }
  }

  const executeReset = (rows: OrganizationRow[], clearSelection: (() => void) | null = null) => {
    const resettableRows = rows.filter((row) => {
      const capability = capabilitiesByOrganization[row.id]

      return capability?.eligible && capability.state === "ready"
    })

    if (!resettableRows.length || submitting) return

    void executeBatch(
      resettableRows.map((row) => row.id),
      clearSelection,
    )
  }

  const extension: OrganizationsPageExtension = {
    enableSelection: true,
    getRowState,
    onOrganizationsLoading: performance.invalidate,
    onOrganizationsLoaded: (organizations) => {
      if (isPlatformAdmin)
        void performance.load(organizations.map((organization) => organization.id))
    },
    setReloadFunction: (reload) => {
      canonicalReload = reload
    },
  }

  onMount(() => {
    void loadCapabilities()
  })

  onDestroy(() => {
    capabilityController?.abort()
    performance.invalidate()
  })

  const additionalColumns = $derived<OrganizationTableColumnExtension[]>([
    {
      definition: {
        id: "kehrblattUsage",
        label: "Verbrauch",
        tooltip:
          "Im Zeitraum eingeplante Aufträge und angefallene Credits. Sortierung: Credits der letzten 30 Tage.",
        sortable: true,
        sortType: "number",
        defaultSortDir: "desc",
        getSortValue: (row) =>
          performance.byOrganization[row.id]?.periods.find((period) => period.days === 30)
            ?.billed_credits ?? null,
      },
      cell: usageCell,
    },
    {
      definition: {
        id: "kehrblattProfit",
        label: "Gewinn (geschätzt)",
        tooltip: `${profitExplanation} Sortierung: Gewinn der letzten 30 Tage.`,
        sortable: true,
        sortType: "number",
        defaultSortDir: "desc",
        getSortValue: (row) =>
          performance.byOrganization[row.id]?.periods.find((period) => period.days === 30)
            ?.estimated_profit_cents ?? null,
      },
      cell: profitCell,
    },
  ])

  const additionalRowActions: TableRowActionsResolver = (row, rowIndex) => {
    const actions: TableRowAction[] = []

    const capability = capabilitiesByOrganization[row.id]

    actions.push({
      id: "action-0",
      label: adminDemoResetCapabilityReason(capability),
      onClick: () => {
        executeReset([row as OrganizationRow])
      },
      icon: mdiRestore,
      disabled:
        submitting ||
        capabilityLoading ||
        capabilityFailed ||
        capability?.eligible !== true ||
        capability.state !== "ready",
      tooltip: adminDemoResetCapabilityReason(capability),
      testId: `kehrblatt.admin.demoReset.row.${row.id}`,
      display: "inline",
    })

    return actions
  }
</script>

{#snippet usageCell(row: OrganizationRow)}
  <OrganizationPerformanceCell
    kind="usage"
    performance={performance.byOrganization[row.id]}
    loading={performance.loading}
    failed={Boolean(performance.error)}
  />
{/snippet}

{#snippet profitCell(row: OrganizationRow)}
  <OrganizationPerformanceCell
    kind="profit"
    performance={performance.byOrganization[row.id]}
    loading={performance.loading}
    failed={Boolean(performance.error)}
  />
{/snippet}

{#if isPlatformAdmin && activeTab === "organizations"}
  {#if performance.error}
    <div role="alert">
      {performance.error}
      <CTA
        type="secondaryButton"
        content="Erneut versuchen"
        onClick={() => void performance.retry()}
      />
    </div>
  {/if}

{/if}

{#snippet batchActions(context: OrganizationTableSelectionContext)}
  <CTA
    type="primaryButton"
    icon={mdiRestore}
    content="Demo-Daten zurücksetzen"
    disabled={submitting}
    onClick={() => executeReset(context.selectedRows, context.clearSelection)}
    dataTestid="kehrblatt.admin.demoReset.batch"
  />
{/snippet}

<OrganizationsPage
  {viewFrameContext}
  {activeTab}
  extension={{
    ...extension,
    batchActions,
    additionalRowActions,
    additionalColumns: isPlatformAdmin ? additionalColumns : [],
    refreshKey: performance.revision,
  }}
/>
